import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getUserRecycleBin,
  restoreUser,
  permanentDeleteUser,
  bulkPermanentDeleteUsers,
} from '../../api/admin';
import { useAuth } from '../../context/AuthContext';
import AdminLayout from '../../components/AdminLayout';
import Alert from '../../components/Alert';
import Button from '../../components/Button';

export default function AdminUserRecycleBinPage() {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const isSuperAdmin = currentUser?.role === 'superadmin';

  const [list, setList] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const load = async () => {
    if (!isSuperAdmin) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { data } = await getUserRecycleBin();
      setList(data);
      setSelected([]);
    } catch (e) {
      setError(e.response?.data?.detail || '加载失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [isSuperAdmin]); // eslint-disable-line

  const handleRestore = async (u) => {
    if (!window.confirm(`确认恢复 ${u.email}？恢复后该用户可重新登录。`)) return;
    setError('');
    setSuccess('');
    try {
      await restoreUser(u.id);
      setSuccess(`已恢复：${u.email}`);
      load();
    } catch (e) {
      setError(e.response?.data?.detail || '恢复失败');
    }
  };

  const handlePermanentDelete = async (u) => {
    if (!window.confirm(`确认永久删除 ${u.email}？此操作不可撤销，所有数据将一并删除。`)) return;
    setError('');
    setSuccess('');
    try {
      await permanentDeleteUser(u.id);
      setSuccess(`已永久删除：${u.email}`);
      load();
    } catch (e) {
      setError(e.response?.data?.detail || '删除失败');
    }
  };

  const handleBulkPermanentDelete = async () => {
    if (selected.length === 0) return;
    if (!window.confirm(`确认永久删除选中的 ${selected.length} 名用户？此操作不可撤销。`)) return;
    setError('');
    setSuccess('');
    try {
      const { data } = await bulkPermanentDeleteUsers(selected);
      const msg = data.skipped > 0
        ? `已永久删除 ${data.deleted} 名用户，跳过 ${data.skipped} 名`
        : `已永久删除 ${data.deleted} 名用户`;
      setSuccess(msg);
      load();
    } catch (e) {
      setError(e.response?.data?.detail || '批量永久删除失败');
    }
  };

  const toggleSelect = (id) => {
    setSelected(s => (s.includes(id) ? s.filter(i => i !== id) : [...s, id]));
  };

  const toggleAll = () => {
    setSelected(selected.length === list.length ? [] : list.map(u => u.id));
  };

  if (!isSuperAdmin) {
    return (
      <AdminLayout title="用户回收站">
        <Alert type="error">用户回收站仅超级管理员可访问。企业管理员只能将本企业成员移出企业，不能删除用户。</Alert>
        <button onClick={() => navigate('/admin/users')} style={backButtonStyle}>
          返回成员管理
        </button>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="用户回收站">
      <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <button onClick={() => navigate('/admin/users')} style={backButtonStyle}>
          返回用户管理
        </button>
        <span style={{ fontSize: 13, color: '#94a3b8' }}>
          共 {list.length} 名已删除用户
        </span>
        {selected.length > 0 && (
          <Button size="sm" variant="danger" onClick={handleBulkPermanentDelete}>
            永久删除所选 ({selected.length})
          </Button>
        )}
      </div>

      {error && <Alert type="error" style={{ marginBottom: 12 }}>{error}</Alert>}
      {success && <Alert type="success" style={{ marginBottom: 12 }}>{success}</Alert>}

      <div style={noticeStyle}>
        回收站仅供超管处理软删除用户。恢复会重新启用账号；永久删除不可撤销。
      </div>

      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#f8fafc' }}>
              <th style={{ ...thStyle, width: 40 }}>
                <input
                  type="checkbox"
                  checked={selected.length === list.length && list.length > 0}
                  onChange={toggleAll}
                  disabled={list.length === 0}
                />
              </th>
              {['ID', '邮箱', '显示名', '企业', '删除时间', '删除操作人', '操作'].map(h => (
                <th key={h} style={thStyle}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} style={emptyCellStyle}>加载中...</td></tr>
            ) : list.length === 0 ? (
              <tr><td colSpan={8} style={emptyCellStyle}>回收站为空</td></tr>
            ) : list.map(u => (
              <tr key={u.id} style={{
                borderBottom: '1px solid #f1f5f9',
                background: selected.includes(u.id) ? '#fef9f0' : '#fff',
              }}>
                <td style={{ padding: '10px 14px' }}>
                  <input
                    type="checkbox"
                    checked={selected.includes(u.id)}
                    onChange={() => toggleSelect(u.id)}
                  />
                </td>
                <td style={mutedTdStyle}>{u.id}</td>
                <td style={tdStyle}>{u.email}</td>
                <td style={{ ...tdStyle, color: '#64748b' }}>{u.display_name || '-'}</td>
                <td style={{ ...tdStyle, color: '#64748b', fontSize: 12 }}>{u.enterprise_name || '-'}</td>
                <td style={mutedTdStyle}>
                  {u.deleted_at ? new Date(u.deleted_at).toLocaleString('zh-CN') : '-'}
                </td>
                <td style={mutedTdStyle}>{u.deleted_by_email || '-'}</td>
                <td style={{ ...tdStyle, display: 'flex', gap: 6 }}>
                  <button onClick={() => handleRestore(u)} style={restoreButtonStyle}>
                    恢复
                  </button>
                  <button onClick={() => handlePermanentDelete(u)} style={deleteButtonStyle}>
                    永久删除
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </AdminLayout>
  );
}

const backButtonStyle = {
  padding: '7px 14px',
  borderRadius: 8,
  border: '1px solid #e2e8f0',
  background: '#fff',
  color: '#64748b',
  fontSize: 13,
  cursor: 'pointer',
  fontWeight: 500,
  marginTop: 12,
};
const noticeStyle = {
  background: '#fffbeb',
  border: '1px solid #fde68a',
  borderRadius: 10,
  padding: '12px 16px',
  marginBottom: 16,
  fontSize: 13,
  color: '#92400e',
};
const thStyle = {
  padding: '12px 14px',
  textAlign: 'left',
  fontWeight: 600,
  color: '#475569',
  borderBottom: '1px solid #e2e8f0',
};
const tdStyle = { padding: '10px 14px', verticalAlign: 'middle' };
const mutedTdStyle = { ...tdStyle, color: '#94a3b8', fontSize: 12 };
const emptyCellStyle = { padding: 32, textAlign: 'center', color: '#94a3b8' };
const restoreButtonStyle = {
  padding: '4px 10px',
  borderRadius: 6,
  fontSize: 12,
  cursor: 'pointer',
  fontWeight: 500,
  border: '1px solid #16a34a30',
  background: '#f0fdf4',
  color: '#16a34a',
};
const deleteButtonStyle = {
  ...restoreButtonStyle,
  border: '1px solid #dc262630',
  background: '#fef2f2',
  color: '#dc2626',
};
